import { ArrowRight } from "lucide-react";
import { useLang } from "../i18n";
import { scrollToId } from "../smooth";
import { Reveal } from "./Reveal";

const TextCell = ({ chapter, title, children, cta, dark, testid, id }) => {
    const target = dark ? "#request" : "#story";
    return (
    <div
        id={id}
        data-testid={testid}
        className={`flex flex-col items-center justify-center px-8 py-14 text-center md:px-12 lg:py-0 ${
            dark ? "bg-navy text-ivory" : "bg-ivory-panel text-ink"
        }`}
    >
        <Reveal>
            <span className="text-[10px] tracking-[0.4em] text-champagne uppercase">{chapter}</span>
            <h2
                className={`mt-4 font-serif text-2xl leading-snug tracking-[0.14em] uppercase md:text-[26px] ${
                    dark ? "text-champagne" : "text-ink"
                }`}
            >
                {title}
            </h2>
            <div className="mt-4 flex items-center justify-center gap-2.5">
                <span className="h-px w-8 bg-champagne" />
                <span className="h-1 w-1 rotate-45 bg-champagne" />
                <span className="h-px w-8 bg-champagne" />
            </div>
            <div className={`mt-6 space-y-4 text-sm leading-relaxed ${dark ? "text-ivory/75" : "text-ink/75"}`}>
                {children}
            </div>
            <a
                href={target}
                onClick={(e) => {
                    e.preventDefault();
                    scrollToId(target);
                }}
                data-testid={dark ? "request-access-cta" : "discover-story-cta"}
                className={`group mt-8 inline-flex items-center gap-3 text-[11px] tracking-[0.28em] uppercase ${
                    dark ? "text-champagne" : "text-ink"
                }`}
            >
                <span className="border-b border-transparent pb-1 transition-colors duration-500 group-hover:border-champagne">
                    {cta}
                </span>
                <ArrowRight
                    size={14}
                    strokeWidth={1.6}
                    className="text-champagne transition-transform duration-500 group-hover:translate-x-1.5"
                />
            </a>
        </Reveal>
    </div>
    );
};

const PhotoCell = ({ src, alt, testid }) => (
    <div data-testid={testid} className="group relative h-72 overflow-hidden md:h-96 lg:h-auto">
        <img
            src={src}
            alt={alt}
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-[2200ms] ease-out group-hover:scale-[1.05]"
        />
    </div>
);

const Story = () => {
    const { t } = useLang();
    return (
        <section id="story" data-testid="story-section" className="bg-ivory">
            <div className="grid lg:grid-cols-4">
                <TextCell
                    testid="story-text"
                    chapter={t.story.chapter}
                    title={t.story.title}
                    cta={t.story.cta}
                >
                    <p>{t.story.p1}</p>
                    <p>{t.story.p2}</p>
                    <p>{t.story.p3}</p>
                    <p className="font-serif text-base italic">{t.story.tag}</p>
                </TextCell>
                <PhotoCell testid="story-photo" src="/assets/story.jpg" alt="Historic lakeside villas on the Italian lakes" />
                <>
                    <TextCell
                        id="hospitality"
                        testid="hospitality-text"
                        dark
                        chapter={t.hospitality.chapter}
                        title={t.hospitality.title}
                        cta={t.hospitality.cta}
                    >
                        <p>{t.hospitality.p1}</p>
                        <p className="font-serif text-base italic text-champagne">{t.hospitality.emphasis}</p>
                    </TextCell>
                    <PhotoCell
                        testid="hospitality-photo"
                        src="/assets/hospitality.jpg"
                        alt="LARIVA on a fine dining table"
                    />
                </>
            </div>
        </section>
    );
};

export default Story;
